import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { useState } from "react";
import { FilterDrawer } from "../../components/filters/FilterDrawer";
import {
  getDateRangeFilter,
  getInStatusFilter,
  getInitialDateRange,
  getInitialFilterValues,
  SearchInput,
  toSearchInputArray,
} from "../../configuration/filters/searchInput";
import { MultiselectInput, MultiselectValue } from "../../components/Multiselect";
import { paymentStatusRender, publicOrderStatusRender } from "../../utils/searchField.util";
import { DatePickerInput } from "../../components/DatePicker";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../components/filters/filterStyles";
import { useAuthUser } from "../../hooks/useAuthUser";

const filterFormId = "orders-filter-form";

type OrdersFilterProps = {
  filters: SearchInput<GraphQL.OrderSearchField>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.OrderSearchField>[]) => void;
};

export function OrdersFilter({ isOpened, filters, onClose, onApply }: { isOpened: boolean } & OrdersFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} onClose={onClose}>
      <OrdersFilterContent filters={filters} onClose={onClose} onApply={onApply} />
    </FilterDrawer>
  );
}

export function OrdersFilterContent({ filters: initialFilters, onClose, onApply }: OrdersFilterProps) {
  const { classes } = useFilterStyles();
  const user = useAuthUser();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.OrderSearchField.CustomerPublicId, initialFilters),
    },
  });
  const [orderStatusFilter, setOrderStatusFilter] = useState<GraphQL.PublicOrderStatus[]>(() =>
    getInitialFilterValues<GraphQL.OrderSearchField, GraphQL.PublicOrderStatus>(
      GraphQL.OrderSearchField.Status,
      initialFilters
    )
  );
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<GraphQL.PaymentStatus[]>(() =>
    getInitialFilterValues<GraphQL.OrderSearchField, GraphQL.PaymentStatus>(
      GraphQL.OrderSearchField.PaymentStatus,
      initialFilters
    )
  );
  const [orderDate, setOrderDate] = useState<[Date | null, Date | null]>(() =>
    getInitialDateRange(GraphQL.OrderSearchField.CreatedAt, initialFilters)
  );
  const [statusUpdatedDate, setStatusUpdatedDate] = useState<[Date | null, Date | null]>(() =>
    getInitialDateRange(GraphQL.OrderSearchField.UpdatedAt, initialFilters)
  );
  const [submittedDate, setSubmittedDate] = useState<[Date | null, Date | null]>(() =>
    getInitialDateRange(GraphQL.OrderSearchField.SubmittedAt, initialFilters)
  );

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getCustomerPublicIdFilter(GraphQL.OrderSearchField.CustomerPublicId, customerPublicId),
      getInStatusFilter(GraphQL.OrderSearchField.Status, orderStatusFilter),
      getInStatusFilter(GraphQL.OrderSearchField.PaymentStatus, paymentStatusFilter),
      getDateRangeFilter(GraphQL.OrderSearchField.CreatedAt, orderDate),
      getDateRangeFilter(GraphQL.OrderSearchField.UpdatedAt, statusUpdatedDate),
      getDateRangeFilter(GraphQL.OrderSearchField.SubmittedAt, submittedDate),
    ]);

    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
    setOrderStatusFilter([]);
    setPaymentStatusFilter([]);
    setOrderDate([null, null]);
    setStatusUpdatedDate([null, null]);
    setSubmittedDate([null, null]);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        {user.is.SUPER_ADMIN && <OrganizationFilter {...getInputProps("selectedCustomer")} />}
        <MultiselectInput
          label="Order Status"
          items={allOrderStatuses}
          value={orderStatusFilter.map((value) => ({ id: value, label: publicOrderStatusRender(value) }))}
          onValueChange={(newValue) => {
            setOrderStatusFilter(newValue.map((value) => value.id));
          }}
          onDelete={(valueToDelete) => {
            setOrderStatusFilter((currentStatuses) => {
              const filteredStatuses = currentStatuses.filter((status) => status !== valueToDelete.id);
              return filteredStatuses;
            });
          }}
        />
        <MultiselectInput
          label="Payment Status"
          items={allPaymentStatuses}
          value={paymentStatusFilter.map((value) => ({ id: value, label: paymentStatusRender(value) }))}
          onValueChange={(newValue) => {
            setPaymentStatusFilter(newValue.map((value) => value.id));
          }}
          onDelete={(valueToDelete) => {
            setPaymentStatusFilter((currentStatuses) => {
              const filteredStatuses = currentStatuses.filter((status) => status !== valueToDelete.id);
              return filteredStatuses;
            });
          }}
        />
        <DatePickerInput
          label="Order Date"
          range
          initialMonth={orderDate?.[0] || undefined}
          value={orderDate}
          onChange={setOrderDate}
        />
        <DatePickerInput
          label="Status Updated Date"
          range
          initialMonth={statusUpdatedDate?.[0] || undefined}
          value={statusUpdatedDate}
          onChange={setStatusUpdatedDate}
        />
        <DatePickerInput
          label="Submitted Date"
          range
          initialMonth={submittedDate?.[0] || undefined}
          value={submittedDate}
          onChange={setSubmittedDate}
        />
      </form>
    </Stack>
  );
}

const allOrderStatuses: MultiselectValue<GraphQL.PublicOrderStatus>[] = Object.values(GraphQL.PublicOrderStatus).map(
  (status) => ({ id: status, label: publicOrderStatusRender(status) })
);

const allPaymentStatuses: MultiselectValue<GraphQL.PaymentStatus>[] = Object.values(GraphQL.PaymentStatus).map(
  (status) => ({ id: status, label: paymentStatusRender(status) })
);
